import { Button, TextField, useMediaQuery, useTheme } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { GridToolbarContainer } from '@mui/x-data-grid';
import { ConnectionState } from '@nemuru-eng/js-api-client';

import { CustomExport } from '@/components/shared/components/FetchingDataGrid/components/CustomExport';
import { LiveIndicator } from '@/components/shared/components/LiveIndicator';
import { StatusTag } from '@/components/shared/components/StatusTag/StatusTag';
import { StatusTagType } from '@/config/types';
import { useScreen } from '@/hooks/useScreen';

interface CustomToolbarProps {
  showSearch: boolean;
  showExport: boolean;
  showRefresh: boolean;
  value: string | undefined;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  searchState: { searchString: string | undefined };
  onClick: (searchString?: string) => void;
  booleanFilterTools?: React.ReactNode;
  onClickDownload: () => void;
  exportLoading: boolean;
  exportDisabled?: boolean;
  liveState?: ConnectionState;
  pendingChanges?: number;
  onApplyPendingChanges?: () => void;
}

export const CustomToolbar = ({
  showSearch,
  showExport,
  showRefresh,
  value,
  onChange,
  searchState,
  onClick,
  booleanFilterTools,
  onClickDownload,
  exportLoading,
  exportDisabled,
  liveState,
  pendingChanges,
  onApplyPendingChanges,
}: CustomToolbarProps) => {
  const { isMobile } = useScreen();
  const { breakpoints } = useTheme();
  const isSmallViewport = useMediaQuery(breakpoints.down('sm'), { noSsr: true });

  const useStyles = makeStyles({
    root: {
      '&.MuiDataGrid-toolbarContainer': {
        justifyContent: 'space-between',
      },
    },
    searchFieldSmall: {
      '& .MuiInput-underline:before': {
        bottom: 6,
      },
      '& .MuiInput-underline:after': {
        bottom: 6,
      },
    },
  });

  const classes = useStyles();

  return (
    <>
      <GridToolbarContainer className={classes.root}>
        <div style={{ width: '100%' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {showSearch && (
                <TextField
                  variant="standard"
                  className={isSmallViewport ? classes.searchFieldSmall : ''}
                  value={value ?? ''}
                  onChange={onChange}
                  inputProps={{
                    style: {
                      padding: '14px 0 11px',
                    },
                  }}
                  InputProps={{
                    startAdornment: <i className="material-icons-outlined">search</i>,
                    endAdornment: (
                      <Button
                        disabled={!searchState.searchString}
                        onClick={() => onClick('')}
                        endIcon={<i className="material-icons-outlined">clear</i>}
                        style={{ minWidth: 0 }}
                      />
                    ),
                  }}
                  style={{ minWidth: isMobile ? '100%' : 400 }}
                />
              )}
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {!!booleanFilterTools && booleanFilterTools}
              {showExport && (
                <CustomExport
                  onClickDownload={onClickDownload}
                  exportLoading={exportLoading}
                  disabled={exportDisabled}
                />
              )}
              {liveState !== undefined && (
                <>
                  {pendingChanges ? (
                    <StatusTag
                      type={StatusTagType.INFO}
                      labelTextId="LIVE_PENDING_CHANGES"
                      labelDynamicDataTexts={[pendingChanges >= 99 ? '99+' : String(pendingChanges)]}
                      endIcon="refresh"
                      role="button"
                      onClick={onApplyPendingChanges}
                      style={{ cursor: 'pointer', marginRight: 8 }}
                    />
                  ) : null}
                  <LiveIndicator state={liveState} />
                </>
              )}
              {showRefresh && (
                <Button
                  id={'data-grid-refresh-button'}
                  onClick={() => onClick()}
                  startIcon={
                    <i className="material-icons-outlined" style={{ fontSize: 26 }}>
                      restart_alt
                    </i>
                  }
                  style={{ minWidth: '42px', minHeight: '42px' }}
                />
              )}
            </div>
          </div>
        </div>
      </GridToolbarContainer>
    </>
  );
};
